import { useDevicesList, useUserMedia, watchDeep } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { cloneDeep, isEmpty, isEqual, isObject } from 'lodash';
import { differenceBy, sortBy } from 'lodash';
import { defineStore } from 'pinia';

import { fetchAllRegisteredDevices, fetchMicGainSetting } from '@/api/apiService';
import { useMicAnalyzer } from '@/composables/shared/mic/create/useMicAnalyzer';
import useUserAgent from '@/composables/shared/useDevice';
import { useToastStore } from '@/stores/toast.js';
import { Nullable } from '@/types/common';
import { IMicrophone, IMicrophoneGainSetting, IMicrophoneOptions, IMicrophoneWithVoice } from '@/types/components';
import { MicAnalyzerConfig } from '@/types/composables';
import { IMicGainSetting } from '@/types/response';
import formatMicrophones from '@/utils/chat/microphone/formatMicrophones';
import getScaledLevel from '@/utils/chat/microphone/getScaledLevel';
import {
  MIC_MIN_RADIUS,
  MIC_MAX_RADIUS,
  VOLUME_RATIO,
  DEFAULT_NOISE_FLOOR_CONFIG,
  DEFAULT_CLIPPING_CONFIG,
} from '@/utils/constants/microphone';
import { BUFFER_SIZE, SAMPLE_RATE } from '@/utils/constants/microphone';
import { isMobile } from '@/utils/device';
import { getBrowserAndDeviceInfo } from '@/utils/device';
import truncateToDecimal from '@/utils/truncateToDecimal';
import useQueue from '@/utils/useQueue';

export const useMicrophoneStore = defineStore('microphone', () => {
  const toastStore = useToastStore();
  const { t } = useI18n();

  const userAgent = useUserAgent();
  const micListUpdateQueue = useQueue(); // 마이크 업데이트 큐
  const micGraphRenderQueue = useQueue(); // 마이크 그래프 큐

  /* states */
  // 마이크 스토어 옵션
  const micStoreOptions = ref<IMicrophoneOptions>({
    mode: '',

    isUsingDeviceAutoUpdate: false, // 마이크 장치리스트 갱신 여부 (3초)
    isUsingSpecificGain: false, // 마이크 기기별 게인조정 사용 유무
    isUsingMicWithVoice: false, // 마이크 기기별 음성정보 포함 여부

    isUsingHintBox: false, // 힌트박스 사용유무
    isUsingToast: false, // 토스트 사용유무
  });

  // 마이크 노드
  const isMicOn = ref<boolean>(false);
  const micSourceNode = ref<Nullable<MediaStreamAudioSourceNode>>(null); // 마이크 소스 노드
  const micGain = ref<Nullable<GainNode>>(null); // 마이크 감도
  const micAnalyser = ref<Nullable<AnalyserNode>>(null); // 마이크 분석기
  const micContext = ref<Nullable<AudioContext>>(null); // 마이크 컨테스트
  const micProcessor = ref<Nullable<AudioWorkletNode>>(null); // 마이크 프로세서

  // 마이크 장치 리스트
  const microphones = ref<IMicrophone[]>([]); // 사용 가능 마이크
  const microphonesWithVoice = ref<IMicrophoneWithVoice[]>([]); // 사용 가능한 마이크 + 음성 정보

  // 마이크 이벤트
  const micEvent = ref<Nullable<Event>>(); // 마이크 입출력 이벤트
  const micErrorEvent = ref<Nullable<Event>>(); // 마이크 에러 이벤트
  const micChangeEvent = ref<Nullable<Event>>(); // 마이크 변경 이벤트

  // 마이크 이펙트
  const animationId = ref<Nullable<number>>(null); // 마이크 이팩트 - 애니메이션 ID
  const outerRadius = ref<number>(MIC_MIN_RADIUS); // 마이크 이펙트 - 반지름 값
  const isShowMicEffect = ref<boolean>(false); // 마이크 이펙트 표시 여부

  // 마이크 옵션
  const micSensitivity = ref<number>(5); // 마이크 감도
  const updateDeviceIntervalId = ref(); // 마이크 장치 인터벌
  const loading = ref<boolean>(false); // 마이크 로딩
  const isTssOn = ref<boolean>(true); // TSS 사용유무

  // 마이크 게인 세팅
  const gainSettings = ref<IMicrophoneGainSetting>({
    initialGains: [],
    magnificationGains: [],
  });

  /* getters */
  /**
   * noiseSuppression: filters the audio to remove background noise
   * https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackSettings/noiseSuppression
   * */
  const noiseSuppression = computed<boolean>(() => {
    const { os } = getBrowserAndDeviceInfo();
    return (
      {
        ios: true,
        android: !isTssOn.value,
      }[os] || false
    );
  });

  /**
   * constraints
   * https://developer.mozilla.org/en-US/docs/Web/API/Media_Capture_and_Streams_API/Constraints
   * */
  const audioConstraints = computed(() => {
    return {
      echoCancellation: false,
      noiseSuppression: noiseSuppression.value,
      autoGainControl: false,
      channelCount: 1,
    };
  });

  const micAnalyzerOptions = computed<MicAnalyzerConfig>(() => {
    return {
      noiseFloor: DEFAULT_NOISE_FLOOR_CONFIG,
      clipping: micStoreOptions.value.mode === 'MEETING' ? { threshold: 0.018, density: 0.2 } : DEFAULT_CLIPPING_CONFIG,
    };
  });
  const { isClipping, isNoiseFloor, analyzeMicGain } = useMicAnalyzer(micAnalyzerOptions);

  // 현재 선택한 마이크 정보
  const selectedMicrophone = computed<IMicrophone | IMicrophoneWithVoice>(() => {
    if (!isEmpty(constraints.value) && isObject(constraints.value.audio)) {
      const { deviceId: { exact = '' } = { exact: '' } } = constraints.value.audio;

      const micList = micStoreOptions.value.isUsingMicWithVoice ? microphonesWithVoice.value : microphones.value;

      return micList.find(({ id }) => exact === id) || ({} as IMicrophone);
    }

    return {} as IMicrophone;
  });

  // 마이크 중지상태 여부
  const isSuspendedMic = computed<boolean>(() => {
    const [{ label = '' }] = stream.value?.getTracks() || [{ label: '' }];
    return (
      !!stream.value &&
      !!micContext.value &&
      micContext.value.state === 'suspended' &&
      label === selectedMicrophone.value.label
    );
  });

  const {
    audioInputs, // 마이크 배열
    permissionGranted, // 마이크 권한
    ensurePermissions, // 마이크 권한 요청
  } = useDevicesList({
    requestPermissions: false,
    constraints: {
      video: false,
      audio: audioConstraints.value,
    },
  });

  const {
    stream, // 현재 마이크 스트림
    restart, // 마이크 재시작
    stop, // 마이크 중지
    constraints = ref({
      audio: audioConstraints.value,
    }),
  } = useUserMedia({
    autoSwitch: false,
    constraints: {
      audio: audioConstraints.value,
    },
  });

  /* actions */
  /**
   * @desc 마이크 스토어를 초기화합니다.
   * @param options - 옵션이 없을때는 마이크 스토어를 초기화 시킵니다. 옵션이 있을 때는 설정에 따라 마이크를 준비합니다.
   **/
  const initMicrophoneStore = async (options: IMicrophoneOptions = {} as IMicrophoneOptions) => {
    Object.assign(micStoreOptions.value, {
      mode: options.mode ?? '',
      isUsingDeviceAutoUpdate: !!options.isUsingDeviceAutoUpdate,
      isUsingMicWithVoice: !!options.isUsingMicWithVoice,
      isUsingSpecificGain: !!options.isUsingSpecificGain,
      isUsingHintBox: !!options.isUsingHintBox,
      isUsingToast: !!options.isUsingToast,
    });

    if (updateDeviceIntervalId.value) {
      clearInterval(updateDeviceIntervalId.value);
    }

    if (isEmpty(options)) {
      initMicEvent(null);
      initMicChangeEvent(null);
      await clearMic();
    } else {
      if (micStoreOptions.value.isUsingDeviceAutoUpdate) {
        await updateDevicesAutomatically();
      }
      if (micStoreOptions.value.isUsingSpecificGain) {
        await requestMicGainSetting();
      }
      if (micStoreOptions.value.isUsingMicWithVoice) {
        await requestMicWithVoice();
      }
      await initMic(selectedMicrophone.value.id);
      await updateMicrophones(audioInputs.value);
    }
  };

  /**
   * @desc 마이크 입력시의 이벤트를 세팅합니다.
   * */
  const initMicEvent = (event) => {
    if (event) {
      micEvent.value = event;
    } else {
      micEvent.value = null;
    }
  };

  const initMicErrorEvent = (event) => {
    if (event) {
      micErrorEvent.value = event;
    } else {
      micErrorEvent.value = null;
    }
  };

  /**
   * @desc 마이크 선택 변경시의 이벤트를 세팅합니다.
   * */
  const initMicChangeEvent = (event) => {
    if (event) {
      micChangeEvent.value = event;
    } else {
      micChangeEvent.value = null;
    }
  };

  /**
   * @desc 마이크 장치를 변경합니다.
   * @param exact - 변경할 마이크의 deviceId
   * */
  const initMic = async (exact: string) => {
    if (constraints.value && isObject(constraints.value.audio) && exact && selectedMicrophone.value.id !== exact) {
      constraints.value.audio = {
        ...constraints.value.audio,
        ...{ deviceId: { exact } },
      };
      micSensitivity.value = 5;
      if (micChangeEvent.value) {
        micChangeEvent.value();
      }
    }
  };

  /**
   * @desc 마이크 시작
   * @param deviceId - 마이크의 deviceId
   * */
  const startMic = async (deviceId: string) => {
    if (isSuspendedMic.value && deviceId === selectedMicrophone.value.id) {
      await restartMic();
    } else {
      await clearMic(false);
      const startMicWithQueue = async () => {
        try {
          await initMic(deviceId);
          await restart();
          if (stream.value) {
            isMicOn.value = true;
            await setupMicGraph(false);
          } else {
            throw new Error();
          }
        } catch {
          if (micErrorEvent.value) {
            micErrorEvent.value();
          }
          toastStore.error(t('reject_mic'));
          await clearMic();
        }
      };
      micGraphRenderQueue.add(startMicWithQueue);
    }
  };

  /**
   * @desc 마이크 재시작
   * */
  const restartMic = async () => {
    const restartMicWithQueue = async () => {
      try {
        const isIOS =
          /iP(hone|od|ad)/.test(navigator.userAgent) ||
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        if (isIOS) {
          throw new Error();
        }

        if (micContext.value && micContext.value.state === 'suspended') {
          await micContext.value.resume();
        }

        if (stream.value) {
          stream.value.getAudioTracks().forEach((t) => (t.enabled = true));
        }

        if (!animationId.value) {
          setupMicEffect();
        }

        isMicOn.value = true;
      } catch {
        await clearMic();
        await startMic(selectedMicrophone.value.id);
      }
    };
    micGraphRenderQueue.add(restartMicWithQueue);
  };

  /**
   * @desc 마이크 일시 정지
   * */
  const stopMic = async () => {
    const stopMicWithQueue = async () => {
      try {
        if (stream.value) {
          stream.value.getAudioTracks().forEach((t) => (t.enabled = false));
        }

        if (animationId.value !== null) {
          cancelAnimationFrame(animationId.value);
          animationId.value = null;
          isShowMicEffect.value = false;
        }

        if (micContext.value && micContext.value.state !== 'suspended') {
          await micContext.value.suspend();
        }

        isMicOn.value = false;
      } catch {
        await clearMic();
      }
    };

    micGraphRenderQueue.add(stopMicWithQueue);
  };

  /**
   * @desc 오디오 트랙 및 오디오 그래프를 해제하고 모든 노드를 초기화
   * */
  const clearMic = async (micOff: boolean = true) => {
    const clearMicWithQueue = async () => {
      stop();

      if (micProcessor.value && micProcessor.value.numberOfOutputs > 0) {
        micProcessor.value.disconnect();
        micProcessor.value.port.close();
        micProcessor.value = null;
      }

      if (animationId.value !== null) {
        cancelAnimationFrame(animationId.value);
        animationId.value = null;
        isShowMicEffect.value = false;
        outerRadius.value = MIC_MIN_RADIUS;
      }

      if (micSourceNode.value && micSourceNode.value.numberOfOutputs > 0) {
        micSourceNode.value.disconnect();
        micSourceNode.value = null;
      }

      if (micGain.value && micGain.value.numberOfOutputs > 0) {
        micGain.value.disconnect();
        micGain.value = null;
      }

      if (micAnalyser.value && micAnalyser.value.numberOfOutputs > 0) {
        micAnalyser.value.disconnect();
        micAnalyser.value = null;
      }

      if (micContext.value && micContext.value.state !== 'closed') {
        try {
          await micContext.value.close();
        } finally {
          micContext.value = null;
        }
      }

      if (micOff) {
        isMicOn.value = false;
      }
    };

    micGraphRenderQueue.add(clearMicWithQueue);
  };

  /**
   * @desc 마이크 감도(게인)를 조절합니다.
   * */
  const controlMicGain = (gain?: { initialGain: number; magnificationGain: number }) => {
    if (micGain.value) {
      const adjustedMicDeviceGain = truncateToDecimal(
        (gain ? 1 : micSensitivity.value * VOLUME_RATIO) *
          ((gain?.initialGain ?? selectedMicrophone.value.initialGain) || 15) *
          ((gain?.magnificationGain ?? selectedMicrophone.value.magnificationGain) || 1)
      );
      micGain.value.gain.value = parseFloat(adjustedMicDeviceGain.toString());
      return micGain.value.gain.value;
    }
  };

  /**
   * @desc 마이크 볼륨 레벨에 따른 시각적 효과(애니메이션)를 설정합니다.
   * */
  const setupMicEffect = () => {
    if (micAnalyser.value) {
      const draw = (): void => {
        animationId.value = requestAnimationFrame(draw);

        isShowMicEffect.value = true;
        let r = getScaledLevel(micAnalyser.value);
        if (r > MIC_MAX_RADIUS) {
          r = MIC_MAX_RADIUS;
        } else if (r < MIC_MIN_RADIUS) {
          r = MIC_MIN_RADIUS;
        }
        outerRadius.value = r;
      };
      draw();
    }
  };

  /**
   * @desc 마이크 오디오 노드 그래프를 구성하고 활성화합니다.
   * */
  const setupMicGraph = async (usingQueue: boolean) => {
    const setupMicGraphWithQueue = async () => {
      if (!stream.value) {
        return;
      }

      try {
        micContext.value = new AudioContext({ sampleRate: SAMPLE_RATE });

        // 01) 오디오 워크렛 모듈 등록
        const modulePath =
          import.meta.env.BASE_URL === '/' ? `/js/processor.js` : `${import.meta.env.BASE_URL}js/processor.js`;
        await micContext.value.audioWorklet.addModule(modulePath);

        // 02) 소스 노드 생성
        micSourceNode.value = micContext.value.createMediaStreamSource(stream.value);

        // 03) 게인 노드 생성
        micGain.value = micContext.value.createGain();
        controlMicGain();

        // 04) 분석기 노드 생성
        micAnalyser.value = micContext.value.createAnalyser();
        micAnalyser.value.fftSize = 1024;

        // 05) 프로세서 노드 생성
        micProcessor.value = new AudioWorkletNode(micContext.value, 'audio-processor', {
          processorOptions: { bufferSize: BUFFER_SIZE },
        });

        // 06) 노드 체인 연결
        micSourceNode.value
          .connect(micGain.value)
          .connect(micAnalyser.value)
          .connect(micProcessor.value)
          .connect(micContext.value.destination);

        // 07) 마이크 효과 설정
        setupMicEffect();

        // 08) 마이크 이벤트 설정
        if (micEvent.value) {
          micProcessor.value.port.onmessage = (e: MessageEvent<Float32Array>) => {
            if (isMicOn.value) {
              micEvent.value(e.data);
              // TODO: 임시코드입니다.
              analyzeMicGain(e.data, false);
            }
          };
          micProcessor.value.port.onmessageerror = async () => {
            toastStore.warn(t('unstable_mic'));
            await clearMic();
          };
        }
      } catch {
        // 내부적인 로직에서 에러난경우
        toastStore.warn(t('error_mic'));
        await clearMic();
        if (micErrorEvent.value) {
          micErrorEvent.value();
        }
      }
    };

    if (usingQueue) {
      micGraphRenderQueue.add(setupMicGraphWithQueue);
    } else {
      await setupMicGraphWithQueue();
    }
  };

  /**
   * @desc 마이크 리스트 업데이트 및 장치변경
   **/
  const updateMicrophones = async (devices: MediaDeviceInfo[]) => {
    const updateMicrophonesWithQueue = async () => {
      const oldMicrophones = cloneDeep(microphones.value);

      // 01) 마이크 정보 등록
      microphones.value = await formatMicrophones(devices, gainSettings.value);

      if (isEqual(microphones.value, oldMicrophones)) {
        return;
      }

      // 02) 마이크 음성정보 등록
      await requestMicWithVoice();

      // 03) 마이크 변경
      try {
        if (microphones.value.length > oldMicrophones.length && oldMicrophones.length === 0) {
          // 03-A) 초기에 받아올때
          const { id } = microphones.value.find((mic) => mic.default) || {
            id: '',
          };
          if (isMicOn.value) {
            await startMic(id);
          } else {
            await initMic(id);
          }
        } else if (microphones.value.length < oldMicrophones.length) {
          // 03-B) 기기 삭제 되었을때
          const { id } = microphones.value.find((mic) => mic.default) || {
            id: '',
          };
          await switchMicrophone(id);
        } else if (microphones.value.length > oldMicrophones.length) {
          // 03-C) 기기 추가 되었을때
          const [{ id = '' } = { id: '' }] = differenceBy(microphones.value, oldMicrophones, 'id') || [{ id: '' }];
          await switchMicrophone(id);
        }
      } catch (e) {
        console.error('Failed to switch microphone', e);
      }
    };
    micListUpdateQueue.add(updateMicrophonesWithQueue);
  };

  /**
   * @desc 3초마다 장치 리스트를 불러옵니다.
   **/
  const updateDevicesAutomatically = async () => {
    updateDeviceIntervalId.value = setInterval(async () => {
      if (!micStoreOptions.value.isUsingDeviceAutoUpdate && updateDeviceIntervalId.value) {
        clearInterval(updateDeviceIntervalId.value);
      }
      const newAudioInputs = (await navigator.mediaDevices.enumerateDevices()).filter((i) => i.kind === 'audioinput');

      await updateMicrophones(cloneDeep(newAudioInputs));
    }, 3000);
  };

  /**
   * @desc 마이크 음성정보를 마이크 목록에 추가합니다.
   **/
  const requestMicWithVoice = async () => {
    if (micStoreOptions.value.isUsingMicWithVoice) {
      if (microphones.value.length === 0) {
        loading.value = true;
      }

      // 마이크 음성정보
      const { data, success } = await fetchAllRegisteredDevices();

      if (success) {
        microphonesWithVoice.value = [...microphones.value].reduce((acc: IMicrophoneWithVoice[], mic: IMicrophone) => {
          const {
            fetchingVoiceInfo = false,
            deviceId: voiceId = 0,
            voiceUrl = '',
          } = data.find(({ deviceLabel }) => deviceLabel === mic.label) || {};

          acc.push({
            ...mic,
            fetchingVoiceInfo,
            voiceId,
            voiceUrl,
          });

          return acc;
        }, []);
      } else {
        microphonesWithVoice.value = [];
      }

      loading.value = false;
    }
  };

  // 마이크 감도(게인) 정보를 현재 userAgent에 따라, 필터해줍니다.
  const filterGainSettings = (settings: IMicGainSetting[]) => {
    return sortBy(
      settings.filter(
        ({ mode, browser, device }) =>
          (!mode || mode === micStoreOptions.value.mode) && // 모드
          (!browser || browser === userAgent.browserName) && // 브라우저
          (!device || device === userAgent.deviceName) // 기기;
      ),
      [(item) => (item.mode ? 1 : 0), 'micLabel', 'device']
    );
  };

  /**
   * @desc 마이크 감도(게인) 정보를 받아옵니다.
   **/
  const requestMicGainSetting = async () => {
    try {
      const {
        data = {
          defaultGains: [],
          magnifications: [],
        },
      } = await fetchMicGainSetting();

      gainSettings.value = {
        ...gainSettings.value,
        initialGains: filterGainSettings(data.defaultGains),
        magnificationGains: filterGainSettings(data.magnifications),
      };
    } catch {
      console.error('Failed to fetch mic gain settings');
    }
  };

  /**
   * @desc 마이크 장치 변경을 처리합니다.
   **/
  const switchMicrophone = async (deviceId: string) => {
    const micOld = (microphonesWithVoice.value.find((mic) => mic.id === selectedMicrophone.value.id) ||
      {}) as IMicrophoneWithVoice;
    const micNew = (microphonesWithVoice.value.find((mic) => mic.id === deviceId) || {}) as IMicrophoneWithVoice;

    if (isMicOn.value) {
      await startMic(deviceId);
    } else {
      await initMic(deviceId);
    }

    if (micStoreOptions.value.isUsingToast) {
      controlMicSwitchToast(micOld, micNew);
    }
  };

  /**
   * @desc 마이크 장치 변경시, 토스트를 표시합니다..
   **/
  const controlMicSwitchToast = (oldMic: IMicrophoneWithVoice, newMic: IMicrophoneWithVoice) => {
    toastStore.warn(t('change_mic'), 'MIC CHANGE');

    if (micStoreOptions.value.isUsingMicWithVoice) {
      const hasVoiceOld = !!oldMic.voiceId;
      const hasVoiceNew = !!newMic.voiceId;
      const micLabel = `[${newMic.label}]`;

      if (!hasVoiceOld && hasVoiceNew) {
        toastStore.warn(`${micLabel} ${t('change_mic_tss_on')}`, 'TSS');
      } else if (hasVoiceOld && !hasVoiceNew) {
        toastStore.warn(`${micLabel} ${t('change_mic_no_tss')}`, 'TSS');
      }
    }
  };

  const setAutoMicrophoneSensitivity = () => {
    if (constraints.value && isObject(constraints.value.audio)) {
      constraints.value.audio.echoCancellation = !constraints.value.audio.echoCancellation;
      constraints.value.audio.noiseSuppression = !constraints.value.audio.noiseSuppression;
      constraints.value.audio.autoGainControl = !constraints.value.audio.autoGainControl;
    }
  };

  /**
   * @desc audioInputs가 변경될 때 마이크 장치 목록을 업데이트합니다.
   **/
  watchDeep(audioInputs, async (newVal) => {
    if (isMobile()) {
      await updateMicrophones(newVal);
    }
  });

  /**
   * @desc isMicOn 상태가 변경될 때 토스트 메시지를 표시합니다.
   **/
  watchDeep(isMicOn, async (val) => {
    if (val) {
      toastStore.info(t('toast_mic_on'), 'MIC');
    } else {
      toastStore.info(t('toast_mic_off'), 'MIC');
    }
  });

  return {
    /* states */
    isTssOn, // TSS 사용 여부
    micSensitivity, // 마이크 감도
    /* getters */
    microphones, // 사용 가능한 마이크 장치 목록
    microphonesWithVoice, // 음성 정보를 포함한 마이크 장치 목록
    selectedMicrophone, // 현재 선택된 마이크
    isMicOn, // 마이크 활성화 상태
    loading, // 로딩 상태
    outerRadius, // 마이크 효과 반지름 값
    isShowMicEffect, // 마이크 효과 표시 여부
    permissionGranted, // 마이크 권한 승인 여부
    constraints,
    isClipping,
    isNoiseFloor,
    /* actions */
    initMicrophoneStore, // 스토어 초기화
    initMicEvent, // 마이크 이벤트 설정
    initMicErrorEvent, // 마이크 에러 이벤트 설정
    initMicChangeEvent, // 마이크 변경 이벤트 설정
    initMic, // 마이크 장치 설정
    startMic, // 마이크 시작
    stopMic, // 마이크 중지
    clearMic, // 마이크 관련 모든 자원 해제
    controlMicGain, // 마이크 감도 조절
    requestMicWithVoice, // 음성 정보 가져오기
    ensurePermissions, // 권한 요청
    switchMicrophone, // 마이크 장치 변경 처리
    setAutoMicrophoneSensitivity, //자동 마이크 감도 조절
  };
});
